import React, { useState, useEffect, useCallback } from 'react';
import useScrollAnimation from '../../hooks/useScrollAnimation';
import { scrollToSection } from '../../utils/scrollToSection';
import { trackEvent } from '../../utils/analytics';
import './Hero.css';

const Hero = () => {
  const [currentImage, setCurrentImage] = useState(0);
  const [loadedImages, setLoadedImages] = useState(new Set([0])); // Preload first image
  
  const heroImages = [
    { src: '/images/hero/hero1.webp', alt: 'Modern kitchen renovation - Professional remodeling by RC Remodel' },
    { src: '/images/hero/hero2.webp', alt: 'Luxury bathroom transformation - Custom design and installation' },
    { src: '/images/hero/hero3.webp', alt: 'Complete home renovation - Full house remodeling services' },
    { src: '/images/hero/hero4.webp', alt: 'Custom kitchen design - Contemporary cabinetry and countertops' },
    { src: '/images/hero/hero5.webp', alt: 'Elegant bathroom remodel - Walk-in shower and modern fixtures' },
    { src: '/images/hero/hero6.webp', alt: 'Home addition project - Seamless expansion and construction' }
  ];

  // Preload images progressively
  useEffect(() => {
    const preloadImage = (index) => {
      if (loadedImages.has(index)) return;
      
      const img = new Image();
      img.onload = () => {
        setLoadedImages(prev => new Set([...prev, index]));
      };
      img.src = heroImages[index].src;
    };

    // Preload first image immediately
    preloadImage(0);
    
    // Preload second image after a short delay
    const timer1 = setTimeout(() => preloadImage(1), 500);
    
    // Preload remaining images progressively
    const timer2 = setTimeout(() => {
      heroImages.forEach((_, index) => {
        if (index > 1) {
          setTimeout(() => preloadImage(index), index * 300);
        }
      });
    }, 2000);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, []);

  const nextImage = useCallback(() => {
    setCurrentImage((prevImage) => (prevImage + 1) % heroImages.length);
  }, [heroImages.length]);

  useEffect(() => {
    const interval = setInterval(nextImage, 6000); // Change image every 6 seconds
    return () => clearInterval(interval);
  }, [nextImage]);

  const handleQuoteClick = () => {
    trackEvent('cta_click', {
      cta_name: 'hero_get_free_quote',
      cta_location: 'hero'
    });
    scrollToSection('contact');
  };

  const handlePortfolioClick = () => {
    trackEvent('cta_click', {
      cta_name: 'hero_view_our_work',
      cta_location: 'hero'
    });
    scrollToSection('portfolio');
  };

  return (
    <section id="home" className="hero">
      <div className="hero-background" aria-hidden="true">
        {heroImages.map((image, index) => (
          <div
            key={index}
            className={`hero-slide ${index === currentImage ? 'active' : ''} ${loadedImages.has(index) ? 'loaded' : ''}`}
            style={{ 
              backgroundImage: loadedImages.has(index) ? `url(${image.src})` : 'none',
              backgroundColor: loadedImages.has(index) ? 'transparent' : '#1a1a1a'
            }}
            role="img"
            aria-label={index === currentImage ? image.alt : undefined}
            aria-hidden={index !== currentImage}
          />
        ))}
        <div className="hero-overlay" aria-hidden="true"></div>
      </div>
      
      <div className="container">
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="hero-title max-width-430">
              Transform Your Space with&nbsp;  
              <span className="highlight"> RC Remodel</span>
            </h1>
            <p className="hero-description">
              Professional home remodeling services that bring your vision to life. 
              From kitchen renovations to complete home makeovers, we deliver 
              exceptional quality and craftsmanship.
            </p>
            <div className="hero-buttons">
              <button className="btn btn-primary" onClick={handleQuoteClick}>
                Get Free Quote
              </button>
              <button className="btn btn-outline" onClick={handlePortfolioClick}>
                View Our Work
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
